/** @param {Array<{ _id: string, branches?: Array<{ _id: string }> }>} stores */

export function getAllBranchIds(stores) {
  const ids = [];
  for (const s of stores || []) {
    for (const b of s.branches || []) ids.push(String(b._id));
  }
  return ids;
}

/**
 * Maps checked branch ids + optional full-store selections into API scopeType / scopeIds.
 */
export function deriveScopeFromBranchSelection(stores, selectedBranchIds) {
  const sel = new Set((selectedBranchIds || []).map(String));
  const storesWithBranches = (stores || []).filter((s) => (s.branches || []).length > 0);
  if (storesWithBranches.length === 0) {
    return { scopeType: "restaurant", scopeIds: [] };
  }

  const allBranchIds = getAllBranchIds(stores);
  if (allBranchIds.length > 0 && allBranchIds.every((id) => sel.has(id))) {
    return { scopeType: "restaurant", scopeIds: [] };
  }

  const storeIdsFullySelected = [];
  const partialBranchIds = [];
  for (const s of storesWithBranches) {
    const bids = (s.branches || []).map((b) => String(b._id));
    const selectedInStore = bids.filter((id) => sel.has(id));
    if (selectedInStore.length === bids.length) {
      storeIdsFullySelected.push(String(s._id));
    } else {
      partialBranchIds.push(...selectedInStore);
    }
  }

  if (
    partialBranchIds.length === 0 &&
    storeIdsFullySelected.length === storesWithBranches.length
  ) {
    return { scopeType: "restaurant", scopeIds: [] };
  }
  if (partialBranchIds.length === 0 && storeIdsFullySelected.length > 0) {
    return { scopeType: "store", scopeIds: storeIdsFullySelected };
  }
  return { scopeType: "branch", scopeIds: Array.from(sel) };
}

/** Normalize assignment.restaurantId whether populated or raw ObjectId. */
function assignmentAnchorStoreId(assignment) {
  if (!assignment) return "";
  const r = assignment.restaurantId;
  if (r == null) return "";
  if (typeof r === "object" && r._id != null) return String(r._id);
  return String(r);
}

/** Hydrate branch checkboxes from an assignment row. */
export function selectedBranchesFromAssignment(assignment, stores) {
  if (!assignment) return [];
  const st = assignment.scopeType;
  if (st === "restaurant") {
    const anchor = assignmentAnchorStoreId(assignment);
    if (!anchor) return [];
    const store = (stores || []).find((s) => String(s._id) === anchor);
    return (store?.branches || []).map((b) => String(b._id));
  }
  if (st === "branch") return (assignment.scopeIds || []).map(String);
  if (st === "store") {
    const wanted = new Set((assignment.scopeIds || []).map(String));
    const ids = [];
    for (const s of stores || []) {
      if (wanted.has(String(s._id))) {
        for (const b of s.branches || []) ids.push(String(b._id));
      }
    }
    return ids;
  }
  return [];
}

/** Pick newest assignment by updatedAt then createdAt (for display / default role). */
export function pickLatestAssignment(assignments) {
  if (!assignments?.length) return null;
  return [...assignments].sort((a, b) => {
    const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return tb - ta;
  })[0];
}

/** True when every assignment is full restaurant scope (non-empty list). */
export function assignmentsAllRestaurantScope(assignments) {
  const arr = assignments || [];
  return arr.length > 0 && arr.every((a) => a.scopeType === "restaurant");
}

/** Union of branch ids implied by all assignments (for edit UI). */
export function aggregateBranchIdsFromAssignments(assignments, stores) {
  const branchSet = new Set();
  for (const a of assignments || []) {
    for (const id of selectedBranchesFromAssignment(a, stores)) {
      branchSet.add(id);
    }
  }
  return Array.from(branchSet);
}
